"use client";

import { useEffect, useRef, useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

const DURATIONS = [1, 3, 5];
const CHARS_PER_WORD = 5;

function shuffledPrompt(sentences: readonly string[]): string {
  const items = sentences.slice();
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  const onePass = items.join(" ");
  return `${onePass} ${onePass} ${onePass}`;
}

function scoreTyped(
  prompt: string,
  typed: string
): {
  correct: number;
  errors: number;
} {
  let correct = 0;
  let errors = 0;
  for (let i = 0; i < typed.length; i++) {
    if (typed[i] === prompt[i]) {
      correct++;
    } else {
      errors++;
    }
  }
  return { correct, errors };
}

function computeWpm(correctChars: number, elapsedSeconds: number): number {
  if (elapsedSeconds <= 0) {
    return 0;
  }
  const minutes = elapsedSeconds / 60;
  return Math.round(correctChars / CHARS_PER_WORD / minutes);
}

function computeAccuracy(correct: number, typedLength: number): number {
  if (typedLength === 0) {
    return 100;
  }
  return Math.round((correct / typedLength) * 100);
}

function formatClock(totalSeconds: number): string {
  const clamped = Math.max(0, totalSeconds);
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function charClass(expected: string, actual: string | undefined): string {
  if (actual === undefined) {
    return "text-muted-foreground";
  }
  if (actual === expected) {
    return "text-green-600 dark:text-green-400";
  }
  return "bg-red-500/10 text-red-600 dark:text-red-400";
}

function PromptText({ prompt, typed }: { prompt: string; typed: string }) {
  const visible = prompt.slice(0, typed.length + 400);
  return (
    <p className="whitespace-pre-wrap font-mono text-base leading-relaxed">
      {visible.split("").map((char, index) => (
        <span
          className={charClass(char, typed[index])}
          key={`c-${String(index)}`}
        >
          {char}
        </span>
      ))}
    </p>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const TYPING_SPEED_TEST_STRINGS = {
  testDuration: "Test duration",
  minute: "one:minute|other:minutes",
  timeLeft: "Time left",
  wpm: "WPM",
  accuracy: "Accuracy",
  errors: "Errors",
  typingAreaLabel:
    "Type the text above here — the timer starts on your first keystroke",
  typingPlaceholder: "Start typing to begin the test…",
  pastingDisabled: "Pasting is disabled to keep results fair.",
  yourResults: "Your Results",
  wordsPerMinute: "Words per minute",
  correctCharacters: "Correct characters",
  restartWithNewSentences: "Restart with New Sentences",
  restart: "Restart",
  sentences: [
    "The library stays open late during exam week so students can study together.",
    "A gentle rain fell on the quiet town as the streetlights flickered on.",
    "She packed her bag carefully before the long train ride to the coast.",
    "Good writing takes practice, patience, and a willingness to revise.",
    "The museum's new exhibit features fossils discovered in the desert last year.",
    "He learned to bake bread from his grandmother one warm summer afternoon.",
    "Every map tells a story about the people who drew it.",
    "The orchestra tuned their instruments as the audience found their seats.",
    "Fresh snow covered the hills, and the whole valley seemed to glow.",
    "Reading a little every day builds vocabulary faster than cramming.",
    "The scientists recorded the temperature of the lake every morning at dawn.",
    "A curious fox trotted across the field and vanished into the hedgerow.",
    "The old lighthouse still guides ships safely past the rocky shore.",
    "Teamwork made the school play a success despite the last-minute changes.",
    "Planting a garden teaches patience because nothing grows overnight.",
    "The chess club meets on Thursdays in the room beside the gym.",
    "Her essay argued that small habits shape our lives more than big decisions.",
    "The bakery on the corner sells out of cinnamon rolls by nine.",
    "Volunteers cleaned the beach and sorted the litter into recycling bins.",
    "The hikers reached the summit just as the clouds began to clear.",
    "History books remind us that ordinary people can change the world.",
    "The recipe calls for two eggs, a cup of flour, and a pinch of salt.",
    "Migrating birds travel thousands of miles guided by the stars.",
    "The debate team practiced their arguments until every point felt sharp.",
    "A well-organized desk makes homework feel far less overwhelming.",
    "The ferry crossed the harbor slowly while gulls circled overhead.",
    "Learning an instrument rewires the brain in fascinating ways.",
    "The market was busy with vendors selling fruit, flowers, and warm pastries.",
    "Careful notes taken in class save hours of confusion later.",
    "The marathon runners trained through winter to be ready by spring.",
  ],
} as const;

export default function TypingSpeedTestWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(TYPING_SPEED_TEST_STRINGS);
  const [durationMin, setDurationMin] = useState(1);
  const [prompt, setPrompt] = useState("");
  const [typed, setTyped] = useState("");
  const [timeLeft, setTimeLeft] = useState(60);
  const [finished, setFinished] = useState(false);
  const startAtRef = useRef<number | null>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    setPrompt(shuffledPrompt(strings.sentences));
  }, [strings.sentences]);

  useEffect(() => {
    if (!started || finished) {
      return;
    }
    const totalSeconds = durationMin * 60;
    const id = setInterval(() => {
      const startAt = startAtRef.current;
      if (startAt === null) {
        return;
      }
      const elapsed = Math.round((Date.now() - startAt) / 1000);
      const remaining = Math.max(0, totalSeconds - elapsed);
      setTimeLeft(remaining);
      if (remaining === 0) {
        setFinished(true);
      }
    }, 250);
    return () => clearInterval(id);
  }, [started, finished, durationMin]);

  function handleRestart(nextDuration: number) {
    setDurationMin(nextDuration);
    setPrompt(shuffledPrompt(strings.sentences));
    setTyped("");
    setTimeLeft(nextDuration * 60);
    setFinished(false);
    setStarted(false);
    startAtRef.current = null;
  }

  function handleTypedChange(value: string) {
    if (finished) {
      return;
    }
    if (!started && value.length > 0) {
      setStarted(true);
      startAtRef.current = Date.now();
    }
    setTyped(value.slice(0, prompt.length));
  }

  const { correct, errors } = scoreTyped(prompt, typed);
  const elapsedSeconds = started ? durationMin * 60 - timeLeft : 0;
  const wpm = computeWpm(correct, elapsedSeconds);
  const accuracy = computeAccuracy(correct, typed.length);

  return (
    <>
      {/* Duration picker */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.testDuration}
        </p>
        <div className="flex flex-wrap gap-3">
          {DURATIONS.map((minutes) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={minutes}
            >
              <input
                checked={durationMin === minutes}
                className="accent-primary"
                name="test-duration"
                onChange={() => handleRestart(minutes)}
                type="radio"
                value={minutes}
              />
              {minutes} {tPlural(strings.minute, locale, minutes)}
            </label>
          ))}
        </div>
      </section>

      {/* Live stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label={strings.timeLeft} value={formatClock(timeLeft)} />
        <StatCard label={strings.wpm} value={wpm} />
        <StatCard label={strings.accuracy} value={`${accuracy}%`} />
        <StatCard label={strings.errors} value={errors} />
      </div>

      {/* Prompt */}
      <section className="max-h-64 overflow-y-auto rounded-lg border border-border bg-card p-5">
        <PromptText prompt={prompt} typed={typed} />
      </section>

      {/* Typing area */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="typing-input"
        >
          {strings.typingAreaLabel}
        </label>
        <textarea
          autoComplete="off"
          autoCorrect="off"
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
          disabled={finished}
          id="typing-input"
          onChange={(e) => handleTypedChange(e.target.value)}
          onDrop={(e) => e.preventDefault()}
          onPaste={(e) => e.preventDefault()}
          placeholder={strings.typingPlaceholder}
          spellCheck={false}
          value={typed}
        />
        <p className="text-muted-foreground text-xs">
          {strings.pastingDisabled}
        </p>
      </div>

      {/* Results */}
      {finished && (
        <section className="space-y-4 rounded-lg border border-primary bg-primary/5 p-6">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.yourResults}
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label={strings.wordsPerMinute} value={wpm} />
            <StatCard label={strings.accuracy} value={`${accuracy}%`} />
            <StatCard label={strings.correctCharacters} value={correct} />
            <StatCard label={strings.errors} value={errors} />
          </div>
          <button
            className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
            onClick={() => handleRestart(durationMin)}
            type="button"
          >
            {strings.restartWithNewSentences}
          </button>
        </section>
      )}

      {/* Restart while running */}
      {started && !finished && (
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
          onClick={() => handleRestart(durationMin)}
          type="button"
        >
          {strings.restart}
        </button>
      )}
    </>
  );
}
